import {
	Autocomplete,
	Box,
	Checkbox,
	CircularProgress,
	IconButton,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import CustomTable from './CustomTable';
import {
	getAllStudents,
	getApprovedTeachers,
	getTeacherData,
	sendStudentUpdate,
	updateTeacherData,
} from '../../api/content-api';
import SnackbarContext from '../../store/snackbar-context';
import Loader from '../../ui/Loader';
import Teacher from '../../model/Teacher';
import AuthContext from '../../store/auth-context';
import { useNavigate } from 'react-router-dom';
import { loginPage } from '../../store/fixed-routing';
import Student from '../../model/Student';
import ActionButtons from './StagesTables/ActionButtons';
import dayjs from 'dayjs';
import OpinionScanPreview from './OpinionScanPreview';
import OlympicsContext from '../../store/olympics-context';
import { filter } from './StagesTable';
import EditStudentDialog from './EditStudentDialog';
import { MangedForm } from '../../ui/Form/MangedForm';
import { useCustomTheme } from '../../store/theme-context';
import { debugError } from '../../utils/debug';
import ContactLinks from './ContactLinks';
import TeacherContacts from './TeacherContacts';
import { getTeacherDeclaredStatus } from '../../api/user-api';
import { getTeacherRegistrationStatus } from '../../api/auth-api';
import { registrationPage } from '../../store/fixed-routing';
import { dedupeTeachersById, filterTeacherOptions, teacherOptionLabel } from '../../utils/teacher-autocomplete';
import DeclaredRegistrationStatusIcon, { DeclaredRegStatus } from './DeclaredRegistrationStatusIcon';
import { parseStoredToRows, serializeDeclaredStudents } from '../../utils/declared-students';

type DeclaredStatusPayload = {
	declared: string[];
	registered?: string[];
	matched: { declared: string; studentId?: number; registered: string }[];
	unmatchedDeclared?: string[];
	unmatchedRegistered?: {
		studentId: number;
		firstName: string;
		lastName: string;
		registered: string;
	}[];
};

const normName = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

const StudentsTable = () => {
	const [isLoading, setIsLoading] = useState(true);
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const navigate = useNavigate();
	const { setMsg } = useContext(SnackbarContext);
	const { logout, token, isTeacher, isSecretary, isAdmin, id } = useContext(AuthContext);
	const { olympics, districtFilterId, searchFilterValue, refreshTablesTrigger, setIsRefreshing, isRefreshing } =
		useContext(OlympicsContext);

	const [students, setStudents] = useState<Student[]>([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [teachersChoice, setTeachersChoice] = useState<Teacher[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student | null>();
	const [editedStudentAll, setEditedStudentAll] = useState<Student | null>();
	const [initialApprovedState, setInitialApprovedState] = useState<boolean | null>(null);
	const [inProgress, setInProgress] = useState(false);
	const isRefreshingRef = useRef(false);
	const editable = isAdmin || olympics?.registrationConfirmationFinishDate?.isAfter(dayjs());
	const [declaredStatus, setDeclaredStatus] = useState<DeclaredStatusPayload | null>(null);
	const [addingDeclaredId, setAddingDeclaredId] = useState<number | null>(null);

	const handleCloseEditStudent = () => {
		setEditedStudentAll(null);
	};

	const filterStudents = (list: Student[]) => {
		let filteredStudents = filter(list, districtFilterId);
		filteredStudents = filteredStudents.filter((s) => {
			if (searchFilterValue !== null && searchFilterValue !== undefined && searchFilterValue !== '') {
				const matchesSearch = (value: string) => {
					if (!value) return false;
					return value.toLowerCase().includes(searchFilterValue.toLowerCase());
				};

				return (
					matchesSearch(s.firstName) ||
					matchesSearch(s.lastName) ||
					matchesSearch(s.city) ||
					matchesSearch(s.street) ||
					matchesSearch(s.email) ||
					matchesSearch(s.school?.name || '') ||
					matchesSearch(s.phone) ||
					s.teachers.some((t: Teacher) => matchesSearch(t.firstName) || matchesSearch(t.lastName))
				);
			}
			return true;
		});
		return filteredStudents;
	};

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}

		getAllStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setStudents(students);
					setDisplayedStudents(filterStudents(students));
				} else {
					debugError('StudentsTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	const onSuccess = () => {
		// Silent refresh after edit - don't reset list position
		downloadData(true);
		setEditedStudent(null);
	};

	// Load data on mount or when refresh trigger changes
	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			// First load
			downloadData(false);
		} else {
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger]);

	useEffect(() => {
		if (!token) {
			return;
		}
		if (isTeacher) {
			getTeacherData(token, id)
				.then((response) => {
					if (response.status === 200) {
						const teacher = Teacher.fromApiResponse(response.data);
						setTeachersChoice([teacher]);
					}
				})
				.catch(async (exception) => {
					if (exception.response?.status === 404) {
						try {
							const status = await getTeacherRegistrationStatus(token);
							if (!status?.hasProfileOnLatest) {
								navigate(`${registrationPage}?resume=teacher`);
								return;
							}
						} catch (statusError) {
							debugError('teacher registration status', statusError);
						}
					}
					console.error(exception);
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				});
		} else if (isAdmin || isSecretary) {
			getApprovedTeachers(token)
				.then((response) => {
					if (response.status === 200) {
						const teachers = dedupeTeachersById(response.data.map((d: any) => Teacher.fromApiResponse(d)));
						setTeachersChoice(teachers);
					}
				})
				.catch((exception) => {
					console.error(exception);
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				});
		} else {
			setTeachersChoice([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- load when role/token ready
	}, [token, id, isTeacher, isAdmin, isSecretary, refreshTablesTrigger]);

	useEffect(() => {
		if (students.length > 0) {
			setDisplayedStudents(filterStudents(students));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [districtFilterId, searchFilterValue, students]);

	useEffect(() => {
		if (!isTeacher || !token) {
			return;
		}
		getTeacherDeclaredStatus(token)
			.then((res) => setDeclaredStatus(res.data))
			.catch(async (e) => {
				if (e.response?.status === 404) {
					try {
						const status = await getTeacherRegistrationStatus(token);
						if (!status?.hasProfileOnLatest) {
							navigate(`${registrationPage}?resume=teacher`);
						}
					} catch (statusError) {
						debugError('declared-status redirect', statusError);
					}
					return;
				}
				debugError('declared-status', e);
			});
	}, [isTeacher, token, refreshTablesTrigger, navigate]);

	const refreshDeclaredStatus = useCallback(() => {
		if (!isTeacher || !token) return;
		getTeacherDeclaredStatus(token)
			.then((res) => setDeclaredStatus(res.data))
			.catch((e) => debugError('declared-status refresh', e));
	}, [isTeacher, token]);

	const handleAddDeclaredStudent = async (student: Student) => {
		if (!token || !id || addingDeclaredId !== null) return;
		setAddingDeclaredId(student.id);
		try {
			const teacherRes = await getTeacherData(token, id);
			const teacher = Teacher.fromApiResponse(teacherRes.data);
			const { rows } = parseStoredToRows(teacher.students || '');
			const next = [
				...rows.filter((r) => r.firstName.trim() || r.lastName.trim()),
				{ firstName: student.firstName, lastName: student.lastName },
			];
			await updateTeacherData(token, {
				id: teacher.id,
				personalTitle: teacher.personalTitle,
				academicDegree: teacher.academicDegree,
				firstName: teacher.firstName,
				secondName: teacher.secondName,
				lastName: teacher.lastName,
				category: teacher.category,
				affiliation: teacher.affiliation,
				email: teacher.email,
				phone: teacher.phone,
				city: teacher.city,
				zipCode: teacher.zipCode,
				street: teacher.street,
				building: teacher.building,
				apartment: teacher.apartment,
				declaredStudents: next,
				students: serializeDeclaredStudents(next),
			});
			setMsg({ msg: 'Uczeń został dopisany do listy zadeklarowanych.', severity: 'success' });
			refreshDeclaredStatus();
		} catch (exception: any) {
			console.error(exception);
			if (exception?.response?.status === 401 || exception?.response?.status === 403) {
				logout();
				navigate(loginPage);
				setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
			} else {
				setMsg({
					msg: exception?.response?.data?.message || 'Nie udało się dopisać ucznia do deklaracji.',
					severity: 'error',
				});
			}
		} finally {
			setAddingDeclaredId(null);
		}
	};

	const resolveStudentStatus = (student: Student): DeclaredRegStatus => {
		if (!declaredStatus) return 'add';
		const matchedIds = new Set((declaredStatus.matched || []).map((m) => Number(m.studentId)).filter((n) => n > 0));
		if (matchedIds.has(student.id)) return 'registered';
		const full = normName(`${student.firstName} ${student.lastName}`);
		if ((declaredStatus.matched || []).some((m) => normName(m.registered || m.declared) === full)) {
			return 'registered';
		}
		return 'add';
	};

	const handleApproveChange = () => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				approved: !editedStudent.approved,
			});
		}
	};

	const handleSave = () => {
		setInProgress(true);
		if (inProgress) {
			return;
		}
		sendStudentUpdate(token, editedStudent)
			.then((response) => {
				if (response.status === 200) {
					onSuccess();
					setMsg({ msg: 'Dane zostały zaktualizowane', severity: 'success' });
				} else {
					setMsg({ msg: response.data });
				}
				setInProgress(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setInProgress(false);
			});
	};

	const renderApproveField = (s: Student) => {
		const value = editedStudent?.id === s.id ? editedStudent.approved : s.approved;
		const isEditing = editedStudent?.id === s.id;
		// Sprawdź wynik - jeśli edytujemy, użyj editedStudent, w przeciwnym razie użyj oryginalnego studenta
		const studentToCheck = isEditing && editedStudent ? editedStudent : s;
		// Sprawdź czy uczeń ma rekord wyników w bazie - jeśli result.id istnieje, to rekord jest w bazie
		// Alternatywnie sprawdź czy ma jakiekolwiek wypełnione dane
		const hasResultRecord =
			studentToCheck.result &&
			(studentToCheck.result.id !== undefined ||
				(studentToCheck.result.firstStageStatus && studentToCheck.result.firstStageStatus.trim() !== '') ||
				(studentToCheck.result.firstStagePoints && studentToCheck.result.firstStagePoints > 0) ||
				studentToCheck.result.firstStageQualified === true);

		// Jeśli edytujemy i uczeń ma wynik i był potwierdzony PRZED rozpoczęciem edycji, pokaż komunikat o konsekwencjach
		// Używamy initialApprovedState zamiast aktualnej wartości, aby pokazać komunikat tylko gdy cofamy istniejące potwierdzenie
		if (isEditing && hasResultRecord && value && initialApprovedState === true) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
					<Checkbox
						checked={value}
						disabled={false}
						onChange={handleApproveChange}
					/>
					<Typography
						variant='caption'
						sx={{
							color: 'error.main',
							fontSize: '10px',
							textAlign: 'center',
							maxWidth: '120px',
							lineHeight: 1.2,
						}}
					>
						Uwaga: Cofnięcie potwierdzenia ukryje wyniki i wykluczy z statystyk!
					</Typography>
				</Box>
			);
		}

		return (
			<Checkbox
				checked={value}
				disabled={!isEditing}
				onChange={handleApproveChange}
			/>
		);
	};

	const handleTeachersUpdate = (teachers: Teacher[]) => {
		if (editedStudent) {
			if (teachers.length > 3) {
				setMsg({ msg: 'Uczeń może być przypisany do maksymalnie 3 nauczycieli' });
				return;
			}
			if (isSecretary || isAdmin) {
				setEditedStudent({ ...editedStudent, teachers: teachers });
			} else if (isTeacher) {
				let dif1 = editedStudent?.teachers.filter((item1) => !teachers.some((item2) => item2.id === item1.id));
				dif1 = dif1 ? dif1 : [];
				const dif2 = teachers.filter((item1) => !editedStudent?.teachers.some((item2) => item2.id === item1.id));
				const dif = [...dif1, ...dif2];
				if (dif.every((item) => item.wpUserId === id)) {
					setEditedStudent({ ...editedStudent, teachers: teachers });
				} else {
					setMsg({ msg: 'Brak uprawnień by zmieniać przypisanie innych nauczycieli' });
				}
			}
		}
	};

	const startInlineEdit = (student: Student) => {
		const allowedIds = new Set(teachersChoice.map((t) => t.id));
		setEditedStudent({
			...student,
			teachers: student.teachers.filter((t) => allowedIds.has(t.id)),
		});
		setInitialApprovedState(student.approved);
	};

	const renderEditField = (s: Student) => {
		return isAdmin || isSecretary ? (
			<ActionButtons
				editedStudent={editedStudent}
				student={s}
				isAdmin={isAdmin}
				disabled={!editable}
				handleClose={() => {
					setEditedStudent(undefined);
					setInitialApprovedState(null);
				}}
				handleEdit={() => startInlineEdit(s)}
				handleEditStudent={() => setEditedStudentAll(s)}
				handleSave={handleSave}
				inProgress={inProgress}
			/>
		) : null;
	};

	const renderTeachersField = (s: Student) => {
		const handleCopy = (value: string) => {
			if (!value) {
				return;
			}
			if (navigator?.clipboard?.writeText) {
				navigator.clipboard.writeText(value);
			}
		};

		return s.id === editedStudent?.id ? (
			<Autocomplete
				multiple
				value={editedStudent?.teachers ? editedStudent.teachers : []}
				onChange={(event: any, newValue: Teacher[]) => handleTeachersUpdate(newValue)}
				options={teachersChoice}
				filterOptions={filterTeacherOptions}
				disableClearable
				forcePopupIcon={false}
				getOptionLabel={(t: Teacher) => teacherOptionLabel(t, teachersChoice)}
				isOptionEqualToValue={(option: Teacher, value: Teacher) => option.id === value.id}
				sx={{
					width: '100%',
					fontSize: '14px',
					'& input': {
						width: '100%',
						fontSize: '14px',
						color: palette.primary.main,
					},
					'& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon': {
						padding: '9px !important',
					},
					'& .MuiOutlinedInput-root': {
						width: '100%',
						'& fieldset': {
							border: 'none',
						},
					},
					'& svg': {
						color: isHighContrast ? palette.primary.light + '!important' : 'inherit',
					},
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder='Wybierz nauczyciela'
						sx={{
							width: '100%',
							'& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon': {
								padding: '9px !important',
								backgroundColor: 'red',
							},
						}}
					/>
				)}
				renderOption={(props, option) => (
					<li
						{...props}
						key={option.id}
						style={{ fontSize: '16px', padding: '10px' }}
					>
						{teacherOptionLabel(option, teachersChoice)}
					</li>
				)}
			/>
		) : (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
				{s.teachers.map((t: Teacher) => (
					<Box
						key={t.id}
						sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
					>
						<Typography
							variant='caption'
							fontWeight='medium'
						>
							{`${t.firstName} ${t.lastName}`}
						</Typography>
						{(isAdmin || isSecretary) && (
							<Tooltip
								placement='top'
								componentsProps={{
									tooltip: {
										sx: {
											backgroundColor: 'transparent',
											p: 0,
										},
									},
								}}
								title={
									<TeacherContacts
										teachers={[t]}
										minWidth={220}
									/>
								}
							>
								<IconButton
									size='small'
									sx={{ p: 0.25 }}
									aria-label='Pokaż kontakt nauczyciela'
								>
									<ContactMailIcon sx={{ fontSize: 16 }} />
								</IconButton>
							</Tooltip>
						)}
					</Box>
				))}
			</Box>
		);
	};

	const data = (input: any) => {
		let additionalColumns: any[] = [];

		const contactColumns =
			isSecretary && !isAdmin ? [{ header: 'Kontakt', accessor: 'studentContacts', align: 'left' }] : [];

		if (isAdmin) {
			additionalColumns = [
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Drugie imię', accessor: 'secondName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
				{ header: 'Orzeczenie', accessor: 'specialCertificate', align: 'left', hidden: !(isAdmin || isSecretary) },
				{ header: 'Skan', accessor: 'opinionScan', align: 'left', width: '90px', nosort: true },
				{ header: 'Szkoła', accessor: 'school', align: 'left' },
				{ header: 'Okręg', accessor: 'district', align: 'left', width: '150px' },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
				{ header: 'Nauczyciele', accessor: 'teachers', align: 'left', editable: editable },
				{ header: 'Adres', accessor: 'address', align: 'left', hidden: !(isAdmin || isSecretary) },
				{ header: 'Dane kontaktowe', accessor: 'contactDetails', align: 'left', hidden: !(isAdmin || isSecretary) },
				{
					header: 'Dane do zaświadczenia',
					accessor: 'certificateDetails',
					align: 'left',
					hidden: !(isAdmin || isSecretary),
				},
				{
					header: 'Data i miejsce urodzenia',
					accessor: 'birthDetails',
					align: 'left',
					hidden: !(isAdmin || isSecretary),
				},
			];
		} else {
			additionalColumns = [
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Drugie imię', accessor: 'secondName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
				{ header: 'Orzeczenie', accessor: 'specialCertificate', align: 'left', hidden: !(isAdmin || isSecretary) },
				{ header: 'Skan', accessor: 'opinionScan', align: 'left', width: '90px', nosort: true },
				{ header: 'Szkoła', accessor: 'school', align: 'left' },
				{ header: 'Okręg', accessor: 'district', align: 'left', width: '150px' },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
			];
			if (isSecretary) {
				additionalColumns.push({ header: 'Nauczyciele', accessor: 'teachers', align: 'left', editable: editable });
			}
		}

		return {
			columns: [
				{ header: 'Identyfikator', accessor: 'id', align: 'center', width: '150px' },
				...(isTeacher
					? [{ header: 'Status', accessor: 'declaredRegStatus', align: 'center', width: '90px', nosort: true }]
					: []),
				{ header: '', accessor: 'edit', width: '100px', nosort: true, editable: editable },
				{ header: 'Potwierdzony', accessor: 'approved', align: 'center', width: '150px', editable: editable },
				{
					header: 'Email potwierdzony',
					accessor: 'emailVerified',
					align: 'center',
					width: '150px',
					editable: isAdmin,
				},
				...additionalColumns,
				...contactColumns,
				{ header: 'Deklaracje', accessor: 'declarations', align: 'left', hidden: !(isAdmin || isSecretary) },
			],

			rows: (() => {
				const registeredRows = input
					? input.map((i: Student) => {
							const status = isTeacher ? resolveStudentStatus(i) : null;
							return {
								edit: renderEditField(i),
								declaredRegStatus: status ? (
									<DeclaredRegistrationStatusIcon
										status={status}
										disabled={addingDeclaredId === i.id}
										onAdd={() => handleAddDeclaredStudent(i)}
									/>
								) : null,
								id: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.district.id.toString().padStart(4, '0') + '-' + i.id.toString().padStart(6, '0')}
									</Typography>
								),
								approved: renderApproveField(i),
								emailVerified: (
									<Checkbox
										checked={i.emailVerified}
										disabled
									/>
								),
								personalTitle: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.personalTitle}
									</Typography>
								),
								firstName: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.firstName}
									</Typography>
								),
								secondName: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.secondName}
									</Typography>
								),
								lastName: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.lastName}
									</Typography>
								),
								specialCertificate: (isAdmin || isSecretary) && (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.specialCertificate ? 'Tak' : 'Nie'}
									</Typography>
								),
								opinionScan: i.opinionScan?.hasFile ? <OpinionScanPreview student={i} /> : null,
								school: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.school.name}, {i.school.cityWithCountry}
									</Typography>
								),
								district: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.district.name}
									</Typography>
								),
								schoolClass: (
									<Typography
										variant='caption'
										fontWeight='medium'
									>
										{i.schoolClassText && i.schoolClassText.trim() !== '' ? i.schoolClassText : String(i.schoolClass)}
									</Typography>
								),
								teachers: renderTeachersField(i),
								address: (
									<Typography variant='caption'>
										{`${i.city}, ${i.zipCode}, ${i.street} ${i.building} ${i.apartment !== '' ? '/' + i.apartment : ''}`}
									</Typography>
								),
								studentContacts:
									isSecretary && !isAdmin ? (
										<ContactLinks
											student={i}
											showTeacherContacts={false}
										/>
									) : undefined,
								contactDetails: (isAdmin || isSecretary) && <ContactLinks student={i} />,
								certificateDetails: (isAdmin || isSecretary) && (
									<Typography
										variant='caption'
										dangerouslySetInnerHTML={{
											__html:
												i.certEmail &&
												`email: ${i.certEmail},<br/> adres: ${i.certCity}, ${i.certZipCode}, ${i.certStreet} ${i.certBuilding} ${i.certApartment !== '' ? '/' + i.certApartment : ''}`,
										}}
									></Typography>
								),
								birthDetails: (isAdmin || isSecretary) && (
									<Typography variant='caption'>
										{i.birthdate &&
											`${dayjs(i.birthdate).locale('pl').format('YYYY-MM-DD')} w ${i.birthdatePlace ? i.birthdatePlace : ''}`}
									</Typography>
								),
								declarations: (isAdmin || isSecretary) && (
									<Typography
										variant='caption'
										dangerouslySetInnerHTML={{
											__html: i.declarations.map((d: string) => '- ' + d).join('<br/>'),
										}}
									></Typography>
								),
							};
						})
					: [];

				if (!isTeacher) {
					return registeredRows;
				}

				const unmatched =
					declaredStatus?.unmatchedDeclared ||
					(declaredStatus?.declared || []).filter((name) => {
						const n = normName(name);
						return !(declaredStatus?.matched || []).some(
							(m) => normName(m.declared) === n || normName(m.registered) === n,
						);
					});

				const declaredOnlyRows = unmatched.map((name, idx) => {
					const parts = name.trim().split(/\s+/);
					const firstName = parts[0] || name;
					const lastName = parts.slice(1).join(' ') || '';
					return {
						edit: null,
						declaredRegStatus: <DeclaredRegistrationStatusIcon status='declared' />,
						id: (
							<Typography
								variant='caption'
								color='text.secondary'
							>
								—
							</Typography>
						),
						approved: null,
						emailVerified: null,
						personalTitle: null,
						firstName: (
							<Typography
								variant='caption'
								fontWeight='medium'
							>
								{firstName}
							</Typography>
						),
						secondName: null,
						lastName: (
							<Typography
								variant='caption'
								fontWeight='medium'
							>
								{lastName}
							</Typography>
						),
						specialCertificate: null,
						opinionScan: null,
						school: (
							<Typography
								variant='caption'
								color='text.secondary'
							>
								tylko zadeklarowany
							</Typography>
						),
						district: null,
						schoolClass: null,
						teachers: null,
						address: null,
						studentContacts: undefined,
						contactDetails: null,
						certificateDetails: null,
						birthDetails: null,
						declarations: null,
						_key: `declared-only-${idx}-${name}`,
					};
				});

				return [...declaredOnlyRows, ...registeredRows];
			})(),
		};
	};

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<>
					{isTeacher && (
						<Typography
							variant='caption'
							color='text.secondary'
							sx={{ display: 'block', mb: 1 }}
						>
							Status: pusty niebieski okrąg — zadeklarowany; zielony check — zarejestrowany; pomarańczowy + — dopisz
							zarejestrowanego do listy uczniów.
						</Typography>
					)}
					<CustomTable data={data(displayedStudents)} />
					{editedStudentAll && (
						<MangedForm>
							<EditStudentDialog
								student={editedStudentAll}
								open={!!editedStudentAll}
								onClose={handleCloseEditStudent}
								onSuccess={() => {
									handleCloseEditStudent();
									downloadData(true); // Silent refresh after edit
								}}
							/>
						</MangedForm>
					)}
				</>
			)}
		</>
	);
};

export default StudentsTable;
